import PoolImage from 'public/images/shipsticks/how-it-works-background.jpg'

import { FAQProps } from 'lib/components/FAQ/FAQ'
import { FeatureListProps } from 'lib/components/FeatureList/FeatureList'
import { FiftyFiftyProps } from 'lib/components/FiftyFifty/FiftyFifty'
import { IconProps } from 'lib/components/Icon/Icon'
import { NarrativeHeroProps } from 'lib/components/NarrativeHero/NarrativeHero'
import { TwoThreeFourItemProps } from 'lib/components/TwoThreeFour/TwoThreeFourItem'
import { IconNames } from 'lib/settings/icons'

import InfoButton from '@/components/InfoButton'

export interface HowItWorksPageContent {
  hero: NarrativeHeroProps
  steps: { header?: string; steps: string[] }
  fifties: FiftyFiftyProps[]
  breaker: string
  feature: FeatureListProps
  videos: {
    header: string | React.ReactNode
    cards: TwoThreeFourItemProps[]
  }
  informationCards: InformationCardProps[]
  faq: FAQProps
}

export interface FiftyFiftyTextFields {
  header: string | React.ReactNode
  subHeader?: string | React.ReactNode
  image: string
  body: React.ReactNode
  eyebrow?: {
    icon: IconProps
    text: string
  }
  button?: {
    text: string
    link: {
      href: string
    }
  }
  showEyebrow?: boolean
}

export interface VideoProps {
  header: string | React.ReactNode
  cards: TwoThreeFourItemProps[]
}

export interface InformationCardProps {
  title: string
  description: string
  link: string
}

const BACKGROUND_IMAGE = typeof PoolImage === 'string' ? PoolImage : PoolImage.src

const hero: NarrativeHeroProps = {
  backgroundImage: BACKGROUND_IMAGE,
  header: (
    <>
      How shipping works
      <br />
      (the easy way)
    </>
  ),
  subHeader:
    'Skip baggage claim by shipping your luggage, gear, and everything else straight ' +
    'to your destination.',
}

// const steps: string[] = ['Book', 'Ship', 'Play']

const steps: { header?: string; steps: string[] } = {
  header: 'Learn how to travel hands-free in three easy steps',
  steps: ['Book', 'Ship', 'Play'],
}

const fiftyTexts: FiftyFiftyTextFields[] = [
  {
    header: (
      <>
        1. Schedule your <strong>shipment.</strong>
      </>
    ),
    image: '/images/shipsticks/how-it-works-1.jpg',
    body: (
      <>
        <p className="snplay:leading-relaxed">
          Easily book online, through the app, or over the phone - whatever works best for you. We
          ship door-to-door to just about anywhere in the world.
          <InfoButton
            tooltipText={
              'Choose one way, round-trip, or even multiple stops. Pro tip: Check that your ' +
              'destination can accept shipments.'
            }
            className="snplay:ml-1"
          />
        </p>
      </>
    ),
    eyebrow: {
      icon: {
        icon: 'number-1-circle' as IconNames,
      },
      text: 'Book',
    },
    button: {
      text: 'Get started',
      link: {
        href: '/book/ship',
      },
    },
    showEyebrow: false,
  },
  {
    header: (
      <>
        2. Pack up your <strong>bags &amp; gear.</strong>
      </>
    ),
    image: '/images/shipsticks/how-it-works-2.jpg',
    body: (
      <>
        <p className="snplay:leading-relaxed">
          Pack your suitcases, boxes, and gear as you would for any trip, then attach your provided
          label. We&apos;ll pick everything up at your home or office, or you can drop it off with
          the designated local carrier if you prefer.
          <InfoButton
            tooltipText={`Print your label from your confirmation email or from your account, or
              email it to the local store of the carrier (FedEx, UPS, or DHL) to print`}
            className="snplay:ml-1"
          />
        </p>
      </>
    ),
    eyebrow: {
      icon: {
        icon: 'number-2-circle' as IconNames,
      },
      text: 'Ship',
    },
    button: {
      text: 'How to pack',
      link: {
        href: '/pack',
      },
    },
    showEyebrow: false,
  },
  {
    header: (
      <>
        3. Meet your bags at your <strong>destination.</strong>
      </>
    ),
    image: '/images/shipsticks/how-it-works-3.jpg',
    body: (
      <>
        <p className="snplay:leading-relaxed">
          Enjoy flying (for a change) as you breeze right past the baggage lines. Complimentary
          insurance, real-time tracking, and an on-time guarantee let you relax from the get-go.
          <InfoButton
            tooltipText={
              "You'll usually find your bags waiting at the front desk or bag room when you " +
              'arrive. For hotels and resorts, check with the concierge'
            }
            className="snplay:ml-1"
          />
        </p>
      </>
    ),
    eyebrow: {
      icon: {
        icon: 'number-3-circle' as IconNames,
      },
      text: 'Play',
    },
    button: {
      text: 'On-time guarantee',
      link: {
        href: '/guarantee',
      },
    },
    showEyebrow: false,
  },
]

const fifties: FiftyFiftyProps[] = fiftyTexts.map((text, index) => ({
  ...text,
  image: {
    src: text.image,
  },
  layout: index % 2 === 0 ? 'image-right' : 'image-left',
  backgroundColor: 'white',
  className: 'snplay:md:gap-6 snplay:lg:gap-12',
}))

const breaker: string = `
  We’re here to make every kind of travel easier, more reliable, and way less stressful.
  That’s why every shipment is backed by the Ship&Play guarantee.
`

const feature: FeatureListProps = {
  header: 'Our guarantee',
  className: 'snplay:py-12',
  items: [
    {
      icon: 'clock',
      header: 'On-time guarantee',
      body: (
        <>
          Pick your travel dates and we’ll have your bags and gear there before you arrive - on
          time, every time, or we’ll make it right.
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '/guarantee',
        },
      },
    },
    {
      icon: 'shield-checkmark',
      header: 'Complimentary coverage',
      body: (
        <>
          Every shipment travels protected. Your luggage, clubs, skis, and gear are covered with
          complimentary insurance, with the option to add more.
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '#',
        },
      },
    },
    {
      icon: 'customer-service',
      header: 'Customer support',
      body: (
        <>
          Real people, ready to help. Our team is on hand seven days a week to answer questions and
          keep your travel plans running smoothly.
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '#',
        },
      },
    },
    {
      icon: 'delivery-truck',
      header: 'Door-to-door service',
      body: (
        <>
          We pick up from your home or office and deliver straight to your destination - so you can
          travel hands-free from your front door onward.
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '#',
        },
      },
    },
  ],
}

const videos: VideoProps = {
  header: (
    <>
      Watch our videos <strong>to learn more</strong>
    </>
  ),
  cards: [
    {
      video: 'https://www.youtube.com/watch?v=1Jh3gaZFBfk',
      header: 'Work and Wander: Make Business Travel Effortless with Luggage Shipping',
      body: (
        <>
          Business travel is part of the job for countless professionals - a chance to network,
          attend conferences, and grow their reach. But frequent trips quickly turn into a
          logistical grind, especially when you’re wrangling heavy bags through crowded airports.
          Shipping your luggage ahead changes all of that. See how arriving hands-free helps you
          show up sharp, focused, and ready for business...
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '#',
        },
      },
    },
    {
      video: 'https://www.youtube.com/watch?v=1Jh3gaZFBfk',
      header: 'Family Travel Made Easy: One Less Thing to Carry',
      body: (
        <>
          Traveling with kids means car seats, strollers, pack-and-plays, and more bags than you
          have hands. Add a security line to the mix and the trip can be over before it begins.
          Shipping your family’s gear ahead lets you move through the airport with everyone in tow
          and nothing weighing you down. See how families ship the bulky stuff and arrive to find it
          already waiting at the hotel...
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '#',
        },
      },
    },
    {
      video: 'https://www.youtube.com/watch?v=1Jh3gaZFBfk',
      header: 'Hit the Slopes Sooner: Ship Your Skis and Snowboard Ahead',
      body: (
        <>
          Dragging skis, boots, and poles through airports and onto shuttles is the least fun part
          of any ski trip. Ship your gear ahead and it’s racked and ready at the lodge when you
          arrive - no oversized baggage fees, no wrestling gear through the terminal. Learn how to
          ship to the mountain and back home again with ease.
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '#',
        },
      },
    },
    {
      video: 'https://www.youtube.com/watch?v=1Jh3gaZFBfk',
      header: 'Cruise Hassle-Free: Send Your Bags Straight to the Ship',
      body: (
        <>
          Nothing dampens embarkation day like hauling a week’s worth of luggage through the port.
          Ship your bags directly to the cruise terminal and step aboard with nothing but your
          carry-on. We handle the heavy lifting so your vacation starts the moment you do - learn
          how to ship to and from the dock.
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '#',
        },
      },
    },
    {
      video: 'https://www.youtube.com/watch?v=KKUCgDL-MmA',
      header: 'Golf Getaways: Send Your Clubs Ahead and Just Play',
      body: (
        <>
          A golf trip should be about the course, not lugging your bag through three airports and
          praying it survives baggage handling. Ship your clubs ahead and they’re waiting at the
          resort or course, ready to go when you are. See how avid golfers ship their clubs out and
          back without ever touching a baggage carousel.
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '#',
        },
      },
    },
  ],
}

const informationCards: InformationCardProps[] = [
  {
    title: 'What can I ship?',
    description:
      'Ship your luggage, boxes, trunks, golf clubs, skis, snowboards, and family gear ' +
      'right to your destination.',
    link: '/what-can-i-ship',
  },
  {
    title: 'Packing guides',
    description:
      'Read step-by-step instructions on how to package your items and securely attach ' +
      'the shipping label.',
    link: '/pack',
  },
  {
    title: 'Our guarantee',
    description: 'Affordable pricing, on-time delivery, and the best service are guaranteed.',
    link: '/guarantee',
  },
]

const faq: FAQProps = {
  subtitle: 'Please choose a topic below to better pinpoint your question you may have.',
  title: (
    <>
      Have questions?
      <br />
      <b>We have answers.</b>
    </>
  ),
  categories: [
    {
      categoryName: 'How it Works',
      categoryId: 1,
    },
    {
      categoryName: 'General Questions',
      categoryId: 2,
    },
    {
      categoryName: 'Shipping & Delivery',
      categoryId: 3,
    },
    {
      categoryName: 'Packing & Preparation',
      categoryId: 4,
    },
    {
      categoryName: 'Pricing & Insurance',
      categoryId: 5,
    },
    {
      categoryName: 'Support & Claims',
      categoryId: 6,
    },
  ],
  items: [
    {
      question: 'What is Ship&Play?',
      answer: (
        <>
          <p>
            Ship&Play is a door-to-door shipping service that helps you travel more easily by
            sending your luggage, golf clubs, skis, boxes, and other gear ahead of your trip. We
            pick up from your home, office, or hotel and deliver directly to your destination.
          </p>

          <p>
            By shipping ahead with Ship&Play, you can skip the stress of hauling heavy bags through
            airports, waiting at baggage claim, and dealing with lost or delayed luggage.
          </p>
        </>
      ),
      categoryName: 'How it Works',
      categoryId: 1,
    },
    {
      question: 'How do I create an account?',
      answer: (
        <>
          Navigate to &apos;Log In&apos; in the top right corner of your screen. You can either
          create a new account or sign in to an existing one, but you will need one to book.
          Creating an account requires no commitment and only takes a few moments to complete.
        </>
      ),
      categoryName: 'How it Works',
      categoryId: 1,
    },
    {
      question: 'Is it free to create an account?',
      answer: (
        <>
          Yes, signing up is completely free! There&apos;s no cost or commitment to create an
          account.
        </>
      ),
      categoryName: 'General Questions',
      categoryId: 2,
    },
    {
      question: 'Who can use Ship&Play?',
      answer: (
        <>
          Anyone can use Ship&Play! Whether you&apos;re a family heading on vacation, a frequent
          business traveler, a skier, a golfer, or shipping on behalf of someone else, our service
          is designed to make travel easier for everyone.
        </>
      ),
      categoryName: 'General Questions',
      categoryId: 2,
    },
    {
      question: 'How do I book a shipment?',
      answer: (
        <>
          Click here to get started. Simply enter your pickup and delivery locations, select your
          travel dates, and choose the service that best fits your needs.
        </>
      ),
      categoryName: 'How it Works',
      categoryId: 1,
    },
    {
      question: 'What happens after I book?',
      answer: (
        <>
          Once you complete your booking, you&apos;ll receive your shipping labels and step-by-step
          instructions.
        </>
      ),
      categoryName: 'How it Works',
      categoryId: 1,
    },
    {
      question: 'How many days in advance should I schedule my gear to arrive?',
      answer: (
        <>
          We suggest scheduling your gear to arrive at least one business day before you need it.
          For example, if your trip begins on Monday, plan to have your bags delivered by the Friday
          before.
        </>
      ),
    },
    {
      question: 'What if my destination isn’t a Ship&Play partner?',
      answer: (
        <>
          That’s ok! You can still use Ship&Play, even if your destination isn’t an official
          partner. We ship door-to-door to homes, offices, hotels, resorts, and just about anywhere
          else that can accept a shipment, so you can enjoy the convenience either way.
        </>
      ),
    },
    {
      question: 'What’s the difference between one-way and round-trip shipping?',
      answer: (
        <>
          One-way shipping means your bags or gear are shipped from your starting point to your
          destination. Round-trip shipping includes sending your gear to your destination and then
          back to your home or original location. There is no cost difference between two one-way
          shipments and a round-trip booking.
        </>
      ),
    },
    {
      question: 'What other items can I ship using Ship&Play?',
      answer: (
        <>
          <p>
            Travel completely hands-free by shipping your luggage, golf clubs, skis, snowboards,
            boxes, and family gear like strollers and car seats. We can accommodate carry-on,
            checked, and oversized luggage.
          </p>
          <p>
            If you’re interested in shipping specialty items, or arranging group or event shipments,
            please contact our team so we can explore how to best accommodate your needs.
          </p>
        </>
      ),
    },
    {
      question: 'Where can I ship my gear?',
      answer: (
        <>
          You can send your shipment to any physical destination of your choosing, with the
          exception of P.O. boxes, airports, and countries we don’t currently service.
        </>
      ),
    },
    {
      question: 'What types of bags/containers are allowed?',
      answer: (
        <>
          Pack your belongings in a suitcase, duffel bag, or box, just like you would for the
          airlines. Sporting gear like golf clubs and skis travels best in a soft travel bag or hard
          case. You may also use a box, but it is not required.
        </>
      ),
    },
    {
      question: 'How should I pack my bags and gear?',
      answer: (
        <>
          Click <a href="/pack">here</a> for more information on packing luggage, golf clubs, skis,
          and other gear.
        </>
      ),
    },
    {
      question: 'Can I lock my domestic shipment?',
      answer: (
        <>
          If you’re shipping domestically within the U.S., your bag may be locked. However, we
          recommend leaving it unlocked to avoid any potential delays.
        </>
      ),
    },
    {
      question: 'Can I lock my international shipment?',
      answer: (
        <>
          No, you cannot lock your bag when shipping internationally. All bags must be unlocked to
          ensure customs inspections can be performed if necessary.
        </>
      ),
    },
    {
      question: 'Are there size or weight limits?',
      answer: (
        <>
          Yes. Please identify your bag’s maximum dimensions (length + width + height) and weight to
          determine which bag size category it fits into. If your shipment exceeds the size or
          weight limits for that category, please{' '}
          <a href="https://www.shipplay.com/contact">contact our team</a> - we’ll be happy to
          provide a custom quote for your shipment. Examples: If you have a suitcase that weighs 24
          lbs with a maximum dimension of 60 inches, it should be booked as Checked Luggage. If you
          have a golf bag that weighs 55 lbs with a maximum dimension of 70 inches, it should be
          booked as an XL Golf Bag. Bag Type Maximum Dimension Maximum Weight Standard Golf Bag 72
          inches 42 lbs XL Golf Bag 79 inches 56 lbs Carry-on Luggage 45 inches 25 lbs Checked
          Luggage 62 inches 50 lbs Oversized Luggage 75 inches 65 lbs
        </>
      ),
    },
    {
      question: 'What happens if my bag is overweight?',
      answer: (
        <>
          If your bag weighs more than the limit for your chosen size, there could be extra fees and
          possible delays. To keep things running smoothly, we recommend sizing up if you’re unsure
          - better safe than sorry!
        </>
      ),
    },

    {
      question: 'What if I can’t weigh my bag?',
      answer: (
        <>
          Give your best estimate when booking, or head to a local FedEx, UPS, or DHL to have your
          bag weighed. If the weight is significantly heavier than expected, additional fees may
          apply.
        </>
      ),
    },
    {
      question: 'How do I attach the shipping labels?',
      answer: (
        <>
          If you&apos;re using a hard travel case or box, attach your label with clear packing tape,
          making sure the barcode is visible and secure. If your bag doesn&apos;t have a hard
          surface, you&apos;ll need to place the label on a luggage baggage tag (also known as a fly
          tag), which you can pick up from your local FedEx, UPS, or DHL location, and attach it to
          the handle. Don&apos;t forget to put a second copy of your shipping label in your bag,
          too.
        </>
      ),
      categoryName: 'Packing & Preparation',
      categoryId: 4,
    },
    {
      question: 'What if I don’t have a printer?',
      answer: (
        <>
          You can email the shipping label to your local FedEx, UPS, or DHL for printing. Or, if
          time allows, for an additional fee we can mail printed labels directly to you - just give
          us a heads-up when placing your order.
        </>
      ),
    },
    {
      question: 'Does Ship&Play provide packaging?',
      answer: (
        <>
          We don’t supply packaging, but no worries - you can simply use your own hard case, soft
          travel bag, or box to ship your gear.
        </>
      ),
    },
    {
      question: 'Do I need a box?',
      answer: (
        <>
          Nope, no box needed! If your luggage is packed in a suitcase or duffle bag, you’re good to
          go. Same goes for sporting gear like golf clubs or skis - as long as it’s in an
          airline-approved soft travel bag or hard case.
        </>
      ),
    },
    {
      question: 'Where do I get a box?',
      answer: (
        <>
          If you have a suitcase, soft travel bag, or hard case, you don’t need a box - that’s the
          beauty of our service. However, if you need a box, you can usually buy one at your local
          UPS or FedEx store for a small fee (prices vary by location). We also sell oversized boxes
          - just reach out to Customer Support to order. Here’s our pricing and shipping times for
          boxes:
          <br />
          Ground Shipping (5–7 Business Days)
          <br />
          1 Box: $29.99
          <br />
          2 Boxes: $34.99
          <br />
          Express Shipping (1–3 Business Days)
          <br />
          1 Box: $39.99
          <br />2 Boxes: $44.99
        </>
      ),
    },
    {
      question: 'Do I have to pack my bag differently if I am shipping internationally?',
      answer: (
        <>
          For international shipments, no locks are allowed, including TSA locks, since customs may
          need to inspect your package.
          <br />
          For luggage, you can pack worn clothing, new clothing with price tags removed, footwear,
          headwear, and undergarments. For sporting gear, you can include items like golf clubs,
          balls, and shoes, or skis, boots, and poles, along with rain gear like umbrellas, jackets,
          and pants.
        </>
      ),
    },
    {
      question: 'What can I include in my shipment?',
      answer: (
        <>
          For luggage, you can pack worn clothing, new clothing with price tags removed, footwear,
          headwear, and undergarments. For sporting gear, you can include items like golf clubs,
          balls, and shoes, or skis, boots, and poles, along with rain gear like umbrellas, jackets,
          and pants.
        </>
      ),
    },
    {
      question: 'What items are prohibited?',
      answer: (
        <>
          Some things are off-limits, like toiletries (soap, shampoo, toothpaste), medications,
          pressurized containers (aerosol cans or CO2 cartridges), cash or negotiable items,
          electronics like laptops, rangefinders, and GPS devices, batteries, alcohol or anything
          containing alcohol (including perfume and cologne), tobacco, firearms and ammunition, and
          food, vitamins, or other consumables.
          <br />
          Before shipping, please remove all price tags. For the full list of prohibited items,
          check out our Terms of Use.
        </>
      ),
    },
    {
      question: 'Can I add luggage to my order?',
      answer: (
        <>
          Yes! Adding pieces of luggage to your order is simple. Just scroll down to the traveler
          section, below the pickup and delivery dates, and you’ll see an option labeled Luggage
          Count. From the dropdown menu, select the number of pieces you want to ship and choose the
          type—carry-on, checked, or oversized bag.
        </>
      ),
    },
    {
      question: 'Can I add hotel check-in information to my shipping label?',
      answer: (
        <>
          Yes, under ‘Other Info’ on the booking page, you can type in any information you would
          like. For example: Confirmation Number XXXXX, Checking in XX/XX/XX, Guest of John Smith,
          etc.
        </>
      ),
    },
    {
      question: 'Who will be picking up my shipment?',
      answer: (
        <>
          One of our trusted logistics partners, either FedEx, UPS, or DHL, will pick up your bags
          and gear. Your shipping label will indicate which carrier will handle your pickup.
        </>
      ),
    },
    {
      question: 'Can I get my bags picked up from my home?',
      answer: (
        <>
          Yes! We can pick your bags and gear up from your home or office - or you can simply drop
          them off at your nearest carrier store for added convenience.
        </>
      ),
    },
    {
      question: 'Is there a fee for dropping off my bags?',
      answer: (
        <>
          FedEx Offices and UPS Stores do not charge fees to drop off. However, some third-party
          pickup and drop-off locations may charge package handling fees. These fees are separate
          from our service and can vary depending on the location. To avoid surprises, we recommend
          calling ahead to check if any fees apply.
        </>
      ),
    },
    {
      question: 'Can I choose my carrier drop-off location?',
      answer: (
        <>
          While we assign your carrier (FedEx, UPS, or DHL) when you book, you’re free to drop off
          your shipment at any local location serviced by that carrier. Click here to find a
          drop-off location near you.
        </>
      ),
    },
    {
      question: 'Do I have to be home when my bags are picked up?',
      answer: (
        <>
          You don’t need to be home for pickup. Just leave your securely packed and labeled shipment
          outside your front door during the pickup window, at your discretion. If you don’t want to
          leave it unattended and can’t be home, you can drop it off at a local FedEx, UPS, or DHL
          store - just make sure it matches the carrier listed on your shipping label.
        </>
      ),
    },
    {
      question: 'Can I cancel my order?',
      answer: (
        <>
          Yes, you may cancel your order using the ‘Edit Shipment’ link in your confirmation email,
          tracking page, or by logging into your account and selecting the shipment you want to
          cancel.
        </>
      ),
    },
    {
      question: 'Can I make changes to my order?',
      answer: (
        <>
          Yes! You can update your order using the ‘Edit Shipment’ link in your confirmation email,
          tracking page, or by logging into your account and selecting the shipment you want to
          modify. Just make sure to do it before your shipment is in transit. Once it’s on the move,
          you’ll need to contact customer support to request changes, and fees may apply.
        </>
      ),
    },
    {
      question: 'How long does delivery take?',
      answer: (
        <>
          Delivery times vary depending on the service level you choose. Ground shipments typically
          arrive within 2 to 6 business days, depending on the route, while expedited options can
          get your gear there even faster - some even as quickly as the next business day. After
          pickup, you’ll receive tracking updates so you can monitor your shipment every step of the
          way.
        </>
      ),
    },
    {
      question: 'Is a signature required upon delivery?',
      answer: (
        <>
          A signature is not required, but it’s ultimately up to the driver’s discretion. If the
          driver considers the delivery area secure or your destination has a shipping/receiving
          area, your shipment will be delivered without a signature. If this is a concern, we
          recommend shipping to a supervised location, like a business, golf course, resort, friend
          or family member, or carrier store (fees may apply).
        </>
      ),
    },
    {
      question: 'Does Ship&Play deliver on weekends?',
      answer: (
        <>
          Deliveries can only be scheduled Monday through Friday to ensure your shipment is handled
          during regular business days when all of our partnered carriers are operating. That said,
          depending on the route, your gear might still arrive on a weekend.
        </>
      ),
    },
    {
      question:
        'Do I need to confirm that the hotel, resort, ' +
        'rental home, or golf course will accept my shipment?',
      answer: (
        <>
          Before placing your order, we do recommend reaching out to your destination to let them
          know about your shipment and your expected arrival date.
        </>
      ),
    },
    {
      question: 'Do hotels, resorts, and other destinations charge to receive/hold my gear?',
      answer: (
        <>
          Policies vary by location. Some properties may charge a fee for receiving or holding
          shipments, so it’s best to check directly with them beforehand.
        </>
      ),
    },
    {
      question:
        'Can I have my bag returned to a different address than the one ' +
        'it was originally shipped from?',
      answer: (
        <>
          That’s easy! Just book two separate one-way shipments - one to send your bag out, and
          another to have it returned to a different address. Don’t worry, the cost is the same as a
          round-trip shipment. This also works if your trip has multiple stops and you need your bag
          sent to different places along the way.
        </>
      ),
    },
    {
      question: 'How do I track my shipment?',
      answer: (
        <>
          We&apos;ll keep you posted with email updates and optional text alerts when your shipment
          is picked up, out for delivery, and delivered. You&apos;ll also find a tracking link in
          your confirmation email. To track through your account, simply:
          <br />
          Visit www.shipplay.com
          <br />
          Log in (top-right corner)
          <br />
          Go to My Account {'>'} Shipments
          <br />
          Select the shipment you&apos;d like to track
        </>
      ),
      categoryName: 'How it Works',
      categoryId: 1,
    },
    {
      question: 'Will I get notifications for delivery updates?',
      answer: (
        <>
          Yes! You’ll receive real-time tracking notifications via email so you always know where
          your shipment is and when to expect delivery. You can opt-in to receive notifications via
          text as well.
        </>
      ),
    },
    {
      question: 'What if my shipment is delayed or lost?',
      answer: (
        <>
          Although delays and losses are rare, if we suspect either might have occurred, our
          customer support team will reach out to you. We’ll promptly investigate and work to
          resolve the issue as quickly as possible.
        </>
      ),
    },
    {
      question: 'Do you ship internationally?',
      answer: (
        <>We service 180 countries and territories, and handle all the tricky customs paperwork.</>
      ),
    },
    {
      question: 'How much does it cost to ship my bags and gear?',
      answer: (
        <>
          The cost to ship your luggage, golf clubs, skis, or other gear varies depending on factors
          like your shipping distance and the service level you choose. To get an exact quote,
          simply enter your pickup and delivery locations on our website or contact our customer
          support team for personalized assistance.
        </>
      ),
      categoryName: 'Pricing & Insurance',
      categoryId: 5,
    },
    {
      question: 'Is insurance included? Can I add more coverage?',
      answer: (
        <>
          Domestic shipments come with $1,000 of insurance for sporting equipment and oversized gear
          and $500 for luggage. International shipments include $500 for either. Need more coverage?
          You can add extra for a small fee. We recommend choosing an amount that reflects the full
          value of your gear and the bag it’s packed in.
        </>
      ),
    },
    {
      question: 'What is covered under insurance?',
      answer: (
        <>
          Ship&Play&apos; shipment insurance protects against complete loss of your shipped item or
          against any physical damage to qualified items that has occurred while in transit. For
          items not shipped in a box, the outside casing is considered the shipping container.
          Please note that general wear and tear, along with damage to protruding parts such as
          wheels, straps, pockets, handles, or other attachments on a travel case or bag, are not
          covered under this protection.
        </>
      ),
      categoryName: 'Pricing & Insurance',
      categoryId: 5,
    },
    {
      question: 'How do I file a damage/missing item claim?',
      answer: (
        <>
          In order to file a damage or missing items claim, a{' '}
          <a href="https://www.shipplay.com/claims">claims form</a> must be submitted to a claims
          representative. Claims requirements are outlined on our claims form.
        </>
      ),
      categoryName: 'Support & Claims',
      categoryId: 6,
    },
    {
      question: 'How long does it take to resolve a claim?',
      answer: (
        <>
          <p>
            A claims specialist will review and file your claim within 1-2 business days. If
            additional information is needed, a claims specialist will contact you by phone or
            email. Otherwise, you will be made aware of your claim being filed.
          </p>
          <p>
            Once your claim has been reviewed and filed, you can expect to receive an approval or
            denial within 14 business days. Once the claim has been filed, it cannot be altered. All
            decisions determined by our 3rd party insurance carrier are final.
          </p>
        </>
      ),
      categoryName: 'Support & Claims',
      categoryId: 6,
    },
    {
      question: 'Can I ship luggage, sporting gear, or oversized items?',
      answer: (
        <>
          Yes! You can ship luggage, golf clubs, skis, snowboards, boxes, and family gear. For
          specialty or oversized items, just contact our team - we&apos;re happy to help arrange
          those shipments.
        </>
      ),
      categoryName: 'Shipping & Delivery',
      categoryId: 3,
    },
    {
      question: 'How do I get a custom quote for special shipments?',
      answer: (
        <>
          To get a custom quote for specialty or oversized shipments, please{' '}
          <a href="https://www.shipplay.com/contact">contact</a> our customer support team with your
          shipment details. We&apos;ll work with you to provide a personalized price and shipping
          plan.
        </>
      ),
      categoryName: 'Pricing & Insurance',
      categoryId: 5,
    },
    {
      question: 'Are there items you cannot ship?',
      answer: (
        <>
          Yes, there are certain items we cannot ship for safety and legal reasons. These include
          hazardous materials, perishable goods, and restricted items as outlined in our shipping
          policy. If you&apos;re unsure whether your item qualifies, please{' '}
          <a href="https://www.shipplay.com/contact">contact</a> us before booking.
        </>
      ),
      categoryName: 'Shipping & Delivery',
      categoryId: 3,
    },
    {
      question: 'How do I contact customer support?',
      answer: (
        <>
          Click <a href="https://www.shipplay.com/contact">here</a> to contact us.
        </>
      ),
      categoryName: 'Support & Claims',
      categoryId: 6,
    },
  ],
  button: {
    moreText: 'View More FAQs',
  },
  variant: 'white',
}

const content: HowItWorksPageContent = {
  faq,
  hero,
  steps,
  fifties,
  breaker,
  feature,
  videos,
  informationCards,
}

export interface ShipplayHowItWorksHeroContent {
  imgUrl: string
  title: {
    prefix: string
    emphasis: string
    suffix?: string
  }
  subheading?: string
  description?: string
}

export interface ShipplayHowItWorksStepContent {
  imgUrl: string
  imgAlt: string
  imgSide: 'left' | 'right'
  className?: string
  icon: 'calendar' | 'luggage-tag' | 'shipping-truck' | 'airplane'
  title: {
    prefix: string
    emphasis: string
    suffix?: string
  }
  body: React.ReactNode
  button: {
    text: string
    to?: string
  }
  /**
   * Optional connector rendered after this step. Passing null indicates the
   * final step (no trailing connector).
   */
  connectorAfter?: { className?: string } | null
}

export interface ShipplayHowItWorksStepsContent {
  steps: ShipplayHowItWorksStepContent[]
}

export interface ShipplayCustomerResourceCard {
  title: string
  description: React.ReactNode
  to: string
  buttonLabel: string
}

export interface ShipplayCustomerResourcesContent {
  heading: {
    prefix: string
    emphasis: string
  }
  cards: ShipplayCustomerResourceCard[]
}

export interface ShipplayHowItWorksIntroContent {
  title: React.ReactNode
  body: React.ReactNode
}

export interface ShipplayHowItWorksContent {
  hero: ShipplayHowItWorksHeroContent
  intro: ShipplayHowItWorksIntroContent
  howItWorksSteps: ShipplayHowItWorksStepsContent
}

export const shipplayContent: ShipplayHowItWorksContent = {
  hero: {
    imgUrl: '/images/shipplay/hero/how-it-works.webp',
    title: {
      prefix: 'We Ship. ',
      emphasis: 'You Play.',
    },
    subheading: 'It’s a match made in travel heaven.',
    description: 'Learn how to ship luggage, family essentials, sports equipment, and more.',
  },
  intro: {
    title: (
      <>
        Ship Ahead, Ship
        <br />
        Home &mdash; <span className="snplay:text-primaryDark">or Both</span>
      </>
    ),
    body: (
      <p>
        We offer two easy ways to skip the baggage claim: ship ahead to your destination, or ship
        home at the end of your trip. New to shipping your luggage? Many travelers start with
        shipping home to see how it all works&mdash;then they&rsquo;re hooked and ship ahead every
        time after.
      </p>
    ),
  },
  howItWorksSteps: {
    steps: [
      {
        imgUrl: '/images/shipplay/hiw-schedule.webp',
        imgAlt: 'Scheduling a luggage pickup online',
        imgSide: 'right',
        className: 'snplay:mb-0',
        icon: 'calendar',
        title: {
          prefix: 'Book',
          emphasis: '',
        },
        body: (
          <>
            <p>
              Easily book online or over the phone. Simply tell us what you&rsquo;re shipping, where
              it&rsquo;s going, and when you need it to arrive. Already at your destination? You can
              ship your luggage home, too.
            </p>
            <p>
              Send your bags one-way, round-trip, or to multiple stops along the way. Be sure to
              check that your destination can accept your shipment.
            </p>
          </>
        ),
        button: { text: 'Book Now', to: '/book/ship' },
        connectorAfter: {},
      },
      {
        imgUrl: '/images/shipplay/hiw-label.webp',
        imgAlt: 'A shipping label being attached to a suitcase',
        imgSide: 'left',
        className: 'snplay:mt-0 snplay:mb-0',
        icon: 'luggage-tag',
        title: {
          prefix: 'Pack',
          emphasis: '',
        },
        body: (
          <>
            <p>
              Your welcome kit arrives with pre-printed labels ready to apply. Pack your bags just
              as you would for airline travel, attach your label, and you&rsquo;re ready to go.
            </p>
            <p>
              If you&rsquo;re booking last-minute or shipping internationally, you may need to print
              your label from your confirmation email or account.
            </p>
          </>
        ),
        button: { text: 'How to Pack', to: '/pack' },
        connectorAfter: { className: 'snplay:-scale-x-100' },
      },
      {
        imgUrl: '/images/shipplay/hiw-pickup.webp',
        imgAlt: "A courier picking up a customer's luggage for shipping",
        imgSide: 'right',
        className: 'snplay:mt-0 snplay:mb-0',
        icon: 'shipping-truck',
        title: {
          prefix: 'Ship',
          emphasis: '',
        },
        body: (
          <p>
            We&rsquo;ll pick up your luggage from your home, office, hotel, or rental. Or, you can
            drop off your bags at a nearby partner location&mdash;whatever works best for you.
          </p>
        ),
        button: { text: 'Find a Location', to: '/drop-off-locator' },
        connectorAfter: {},
      },
      {
        imgUrl: '/images/shipplay/hiw-meet-bags.webp',
        imgAlt: 'A traveler being reunited with their shipped bags at the destination',
        imgSide: 'left',
        className: 'snplay:mt-0',
        icon: 'airplane',
        title: {
          prefix: 'Play',
          emphasis: '',
        },
        body: (
          <p>
            Enjoy flying (for a change) as you breeze by the baggage lines. Complimentary insurance,
            live tracking, and an on-time guarantee let you travel relaxed from the start.
          </p>
        ),
        button: { text: 'Get Started', to: '/book/ship' },
        connectorAfter: null,
      },
    ],
  },
}

export default content
